import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import _, {isEqual} from 'underscore';

import {AnalyticsEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {NAV_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK} from 'ui-library/Colors';
import {SortDirection} from 'ui-library/scrollable/ScrollableTableConstants';

import {NO_REMAINING_SORTS_MESSAGE, Sort} from './SearchConstants';
import SearchParamDropdown from './SearchParamDropdown';
import SortForm, {FieldSection, Sortable} from './SortForm';

const MAX_SORTED_COLUMNS = 5;

interface DropdownBodyProps {
  nodes: Array<{key: string; element: JSX.Element}>;
}

function DropdownBody(props: DropdownBodyProps): JSX.Element {
  if (props.nodes.length === 0) {
    return <div className={css(styles.text)}>No active sorts</div>;
  } else if (props.nodes.length === 1) {
    return (
      <div>
        <div className={css(styles.text, styles.headerText)}>Sort by:</div>
        <div className={css(styles.sortNodeContainer)}>{props.nodes[0].element}</div>
      </div>
    );
  } else {
    return (
      <div>
        <div className={css(styles.text, styles.headerText)}>First sort by:</div>
        <div className={css(styles.sortNodeContainer)}>{props.nodes[0].element}</div>
        <div className={css(styles.text, styles.headerText, styles.thenText)}>Then sort by:</div>
        {props.nodes.slice(1).map((node) => (
          <div key={node.key} className={css(styles.sortNodeContainer)}>
            {node.element}
          </div>
        ))}
      </div>
    );
  }
}

function getSelectableSortables(
  sortables: Array<Sortable>,
  pendingSorts: Array<PendingSort>
): Array<Sortable> {
  return sortables.filter((sortable) => _.all(pendingSorts, (p) => p.sortable?.sortKey !== sortable.sortKey));
}

function getColumnUniqueId(): string {
  return _.uniqueId('column-');
}

function getSortDirection(reverse: boolean | undefined): SortDirection {
  return reverse ? SortDirection.DESCENDING_SORT : SortDirection.ASCENDING_SORT;
}

function toPendingSorts(sortables: Array<Sortable>, sorts: Array<Sort>): Array<PendingSort> {
  const pendingSorts: Array<PendingSort> = [];
  // Prevent same sortable from being added twice as a pending sort
  const sortableKeys: Set<string> = new Set<string>();
  for (const sort of sorts) {
    const index = sortables.findIndex((sortable) => sortable.sortKey === sort.key);
    if (index !== -1) {
      const sortable = sortables[index];
      const sortDirection = getSortDirection(sort.reverse);
      if (sortable.sortDirection) {
        assert(
          sortDirection === sortable.sortDirection,
          `Applied sort and column sort direction disagree for key ${sort.key}`
        );
      }
      if (!sortableKeys.has(sortable.sortKey)) {
        sortableKeys.add(sortable.sortKey);
        pendingSorts.push({key: getColumnUniqueId(), sortable: {...sortable, sortDirection}});
      }
    }
  }
  return pendingSorts;
}

function toSorts(pendingSorts: Array<PendingSort>): Array<Sort> {
  const sorts: Array<Sort> = [];
  for (const pendingSort of pendingSorts) {
    const sortable = pendingSort.sortable;
    if (sortable == null) {
      assert(false, 'sortable of pendingSort cannot be null');
    } else {
      sorts.push({
        key: sortable.sortKey,
        reverse: sortable.sortDirection === SortDirection.DESCENDING_SORT,
      });
    }
  }
  return sorts;
}

function getAddDisabledReason(numColumns: number, numPending: number): string | null {
  if (numColumns === 0) {
    return NO_REMAINING_SORTS_MESSAGE;
  }
  const maxFields = Math.min(MAX_SORTED_COLUMNS, numColumns);
  return numPending >= maxFields
    ? `You can sort at most ${maxFields} ${pluralize(
        maxFields,
        'field'
      )}. Remove or change an existing field.`
    : null;
}

interface PendingSort {
  key: string;
  sortable: Sortable | null;
}

export interface SortDropdownProps {
  onChangeSorts: (sorts: Array<Sort>) => void;
  columns: Array<Sortable>;
  // subSortKeys of multiSorts cannot be represented in column keys
  appliedSorts: Array<Sort>;
  isLoading: boolean;
  trackSortEvent: (event: AnalyticsEvent) => void;
  addSortsDisabled: boolean;
  // Custom section mapping to override default
  mapColumnsToSections?: (columns: Array<Sortable>) => Map<FieldSection, Array<Sortable>>;
  customSortables?: Array<Sortable>;
}

export default function SortDropdown(props: SortDropdownProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingSorts, setPendingSorts] = useState(
    toPendingSorts([...props.columns, ...(props.customSortables ?? [])], props.appliedSorts)
  );
  const {onChangeSorts} = props;
  // Reset pending sort state after applied sort changes from sources other than this component - e.g. the
  // arrows next to the column headers. This could be avoided if we hoist the pending sort state to the parent
  // component via a custom hook or by mixing applied and pending sorts in this component and clearing pending
  // sorts after applying changes.
  React.useEffect(
    () => {
      if (!props.isLoading) {
        const newPendingSorts = toPendingSorts(
          [...props.columns, ...(props.customSortables ?? [])],
          props.appliedSorts
        );
        if (!isEqual(pendingSorts, newPendingSorts)) {
          setPendingSorts(newPendingSorts);
          if (newPendingSorts.length === 0) {
            onChangeSorts([]);
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.columns, props.customSortables, props.appliedSorts, props.isLoading]
  );

  const selectableColumns = getSelectableSortables(props.columns, pendingSorts);
  const selectableCustomSortables = getSelectableSortables(props.customSortables ?? [], pendingSorts);
  const sortParamNodes = pendingSorts.map((sort, index) => ({
    key: sort.key,
    element: (
      <SortForm
        selectedSortable={sort.sortable}
        columns={selectableColumns}
        onRemove={() => {
          props.trackSortEvent(NAV_ANALYTICS_ACTIONS.REMOVED_SORT);
          setPendingSorts([...pendingSorts.slice(0, index), ...pendingSorts.slice(index + 1)]);
        }}
        onChangeSort={(sortable: Sortable) => {
          const newPendingSorts = pendingSorts.slice();
          newPendingSorts[index].sortable = sortable;
          setPendingSorts(newPendingSorts);
        }}
        removeButtonHidden={props.addSortsDisabled}
        customSortables={selectableCustomSortables}
      />
    ),
  }));

  const handleAddParam = (): void => {
    props.trackSortEvent(NAV_ANALYTICS_ACTIONS.ADDED_SORT);
    setPendingSorts([...pendingSorts, {key: _.uniqueId('sort-param-'), sortable: null}]);
  };

  const handleToggleDropdown = (isDropdownOpen: boolean): void => {
    setIsOpen(isDropdownOpen);
    if (isDropdownOpen) {
      return;
    }
    const pendingSortsWithSelectedField = pendingSorts.filter((p) => Boolean(p.sortable));
    setPendingSorts(pendingSortsWithSelectedField);
    const finalSorts = toSorts(pendingSortsWithSelectedField);
    if (!isEqual(props.appliedSorts, finalSorts)) {
      props.onChangeSorts(finalSorts);
    }
  };

  return (
    <TooltipWrapper tooltipTitle={props.isLoading ? 'Loading...' : ''}>
      <SearchParamDropdown
        title={
          sortParamNodes.length > 0
            ? `${sortParamNodes.length} ${pluralize(sortParamNodes.length, 'sort')}`
            : 'Sorting'
        }
        icon="arrow-up-down"
        addText="Sort field"
        addDisabledReason={getAddDisabledReason(
          props.columns.length + (props.customSortables ? props.customSortables.length : 0),
          pendingSorts.length
        )}
        buttonSize={ButtonSize.SMALL}
        buttonStyle={sortParamNodes.length > 0 ? ButtonStyle.TERTIARY_BLUE_POWDER : ButtonStyle.TERTIARY_GRAY}
        buttonDisabled={props.isLoading}
        addButtonHidden={props.addSortsDisabled}
        onAddParam={handleAddParam}
        isOpen={isOpen}
        onToggle={handleToggleDropdown}
      >
        <div className={css(styles.dropdownBodyContainer)}>
          <DropdownBody nodes={sortParamNodes} />
        </div>
      </SearchParamDropdown>
    </TooltipWrapper>
  );
}

const styles = StyleSheet.create({
  dropdownBodyContainer: {
    margin: 10,
    width: 300,
  },
  sortNodeContainer: {
    paddingTop: 8,
  },
  text: {
    color: GRAY_DARK,
  },
  headerText: {
    color: GRAY_DARK,
    paddingBottom: 2,
  },
  thenText: {
    paddingTop: 16,
  },
});
